import React, { useState, useCallback } from 'react';

import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { Modal, Button, Form } from 'react-bootstrap';

import { getCroppedImg } from '../utils/cropImage';

/*
  Circular crop dialog shown after a user picks a new profile photo. Uses
  react-easy-crop for drag-to-pan + zoom, then rasterises the selected round
  region to a JPEG Blob passed back via onCropDone.
*/
function ImageCropModal({ show, imageSrc, onCancel, onCropDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setAreaPixels(pixels);
  }, []);

  const apply = async () => {
    if (!areaPixels) return;

    setProcessing(true);
    try {
      const blob = await getCroppedImg(imageSrc, areaPixels);
      onCropDone(blob);
    } catch (err) {
      console.log('Crop failed:', err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal show={show} onHide={onCancel} centered contentClassName="crop-modal">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-bold">
          ✂️ Adjust your photo
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="crop-stage">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="mt-3">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: '0.9rem' }}>🔍</span>
            <Form.Range
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </div>
          <small className="text-muted">
            Drag to reposition · slide to zoom
          </small>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button
          variant="warning"
          className="fw-semibold"
          onClick={apply}
          disabled={processing || !areaPixels}
        >
          {processing ? 'Applying…' : '✅ Apply'}
        </Button>
      </Modal.Footer>

      <style>{`
        .crop-modal { border-radius: 18px; overflow: hidden; }
        .crop-stage {
          position: relative;
          width: 100%;
          height: 320px;
          background: #0f172a;
          border-radius: 14px;
          overflow: hidden;
        }
      `}</style>
    </Modal>
  );
}

export default ImageCropModal;
